// /app/frontend/src/pages/compliance/questionnaire/QuestionnaireListPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Search, FileText, Users, Send, CheckCircle2, Clock, 
  AlertTriangle, BarChart3, Building2, ChevronRight, Eye,
  ClipboardList, Target
} from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../../components/ui/table';

import { useAuth } from '../../../contexts/AuthContext';
import { useGlobalContext } from '../../../contexts/GlobalContext';
import { questionnaireAPI } from '../../../services/api';

const STATUS_BADGES = {
  draft: { label: 'Draft', variant: 'secondary', icon: FileText },
  scheduled: { label: 'Scheduled', variant: 'outline', icon: Clock },
  sent: { label: 'Sent', variant: 'default', icon: Send },
  in_progress: { label: 'In Progress', variant: 'warning', icon: Clock },
  submitted: { label: 'Submitted', variant: 'success', icon: CheckCircle2 },
  closed: { label: 'Closed', variant: 'secondary', icon: CheckCircle2 },
  finalized: { label: 'Finalized', variant: 'success', icon: Target },
};

export default function QuestionnaireListPage() {
  const navigate = useNavigate();
  const { isDPO, isComplianceReadOnly: isReadOnly } = useAuth();
  const { selectedClientId, selectedEntityId, selectedDepartmentId } = useGlobalContext();

  const [loading, setLoading] = useState(true);
  const [instances, setInstances] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('campaigns');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [instancesRes, campaignsRes] = await Promise.all([
        questionnaireAPI.getInstances(selectedEntityId || undefined, selectedClientId || undefined, selectedDepartmentId || undefined),
        questionnaireAPI.getCampaigns(selectedEntityId || undefined, selectedClientId || undefined, selectedDepartmentId || undefined)
      ]);

      setInstances(instancesRes.data);
      setCampaigns(campaignsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load questionnaires');
    } finally {
      setLoading(false);
    }
  }, [selectedEntityId, selectedClientId, selectedDepartmentId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getStatusBadge = (status) => {
    const config = STATUS_BADGES[status] || STATUS_BADGES.draft;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getProgressColor = (percent) => {
    if (percent >= 100) return 'bg-green-500';
    if (percent >= 50) return 'bg-blue-500';
    if (percent > 0) return 'bg-yellow-500';
    return 'bg-gray-300';
  };

  // v2.0 Improvement 6: campaign-list response-rate column.
  // Thresholds set by client: green ≥75%, amber 40–74%, red <40%.
  const getResponseRate = (campaign) => {
    const total = campaign.total_recipients || 0;
    const submitted = campaign.submitted_count ?? campaign.recipient_stats?.submitted ?? 0;
    if (total <= 0) return { percent: 0, submitted: 0, total: 0, band: 'empty' };
    const percent = Math.round((submitted / total) * 100);
    let band = 'red';
    if (percent >= 75) band = 'green';
    else if (percent >= 40) band = 'amber';
    return { percent, submitted, total, band };
  };

  const RESPONSE_RATE_STYLES = {
    green: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800',
    amber: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
    red: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
    empty: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
  };

  // Stats calculation
  const stats = {
    totalCampaigns: campaigns.length,
    activeCampaigns: campaigns.filter(c => ['sent', 'in_progress'].includes(c.status)).length,
    totalInstances: instances.length,
    pendingResponses: campaigns.reduce((sum, c) => sum + (c.recipient_stats?.in_progress || 0), 0),
  };

  const filteredCampaigns = campaigns.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredInstances = instances.filter(i =>
    i.recipient_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.recipient_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="questionnaire-list-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Staff Assessments</h1>
            <p className="text-muted-foreground">TR2UST questionnaire campaigns and individual assessments</p>
          </div>
          <div className="flex items-center gap-3">
            {!isReadOnly && (
              <Button onClick={() => navigate('/compliance/questionnaires/campaign/new')} data-testid="new-campaign-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Campaign
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Campaigns</p>
                  <p className="text-2xl font-bold">{stats.totalCampaigns}</p>
                </div>
                <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Campaigns</p>
                  <p className="text-2xl font-bold">{stats.activeCampaigns}</p>
                </div>
                <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                  <Send className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Individual Assessments</p>
                  <p className="text-2xl font-bold">{stats.totalInstances}</p>
                </div>
                <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Responses</p>
                  <p className="text-2xl font-bold">{stats.pendingResponses}</p>
                </div>
                <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-lg">
                  <Clock className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between mb-4">
            <TabsList>
              <TabsTrigger value="campaigns" className="gap-2">
                <Users className="h-4 w-4" />
                Campaigns ({campaigns.length})
              </TabsTrigger>
              <TabsTrigger value="instances" className="gap-2">
                <FileText className="h-4 w-4" />
                Individual ({instances.length})
              </TabsTrigger>
            </TabsList>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Campaigns Tab */}
          <TabsContent value="campaigns">
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Campaign Name</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Recipients</TableHead>
                      <TableHead>Response Rate</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8">Loading...</TableCell>
                      </TableRow>
                    ) : filteredCampaigns.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          No campaigns found. Create one to get started.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCampaigns.map(campaign => (
                        <TableRow 
                          key={campaign.id} 
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/compliance/questionnaires/campaign/${campaign.id}`)}
                        >
                          <TableCell className="font-medium">{campaign.name}</TableCell>
                          <TableCell>{campaign.entity_name || '-'}</TableCell>
                          <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">
                                {campaign.recipient_stats?.submitted || 0}/{campaign.total_recipients}
                              </span>
                              {campaign.recipient_stats?.in_progress > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  {campaign.recipient_stats.in_progress} in progress
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const rr = getResponseRate({
                                total_recipients: campaign.total_recipients,
                                submitted_count: campaign.recipient_stats?.submitted,
                              });
                              if (rr.band === 'empty') {
                                return <span className="text-muted-foreground text-sm">—</span>;
                              }
                              return (
                                <span
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-semibold ${RESPONSE_RATE_STYLES[rr.band]}`}
                                  title={`${rr.submitted} of ${rr.total} recipients have submitted`}
                                >
                                  {rr.percent}%
                                  <span className="font-normal opacity-75">({rr.submitted}/{rr.total})</span>
                                </span>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <div className="w-24">
                              <div className="flex justify-between text-xs mb-1">
                                <span>{Math.round((campaign.recipient_stats?.submitted || 0) / (campaign.total_recipients || 1) * 100)}%</span>
                              </div>
                              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${getProgressColor(Math.round((campaign.recipient_stats?.submitted || 0) / (campaign.total_recipients || 1) * 100))}`}
                                  style={{ width: `${Math.round((campaign.recipient_stats?.submitted || 0) / (campaign.total_recipients || 1) * 100)}%` }}
                                />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {campaign.campaign_score ? (
                              <div className="flex items-center gap-2">
                                <BarChart3 className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">{campaign.campaign_score.toFixed(1)}%</span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(campaign.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/compliance/questionnaires/campaign/${campaign.id}`); }}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Individual Instances Tab */}
          <TabsContent value="instances">
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
                      </TableRow>
                    ) : filteredInstances.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No individual assessments found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredInstances.map(instance => (
                        <TableRow 
                          key={instance.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/compliance/questionnaires/instance/${instance.id}`)}
                        >
                          <TableCell>
                            <div>
                              <p className="font-medium">{instance.recipient_name || 'Unknown'}</p>
                              <p className="text-sm text-muted-foreground">{instance.recipient_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>{instance.entity_name || '-'}</TableCell>
                          <TableCell>{getStatusBadge(instance.status)}</TableCell>
                          <TableCell>
                            <div className="w-24">
                              <div className="flex justify-between text-xs mb-1">
                                <span>{instance.progress_percent?.toFixed(0) || 0}%</span>
                              </div>
                              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${getProgressColor(instance.progress_percent || 0)}`}
                                  style={{ width: `${instance.progress_percent || 0}%` }}
                                />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {instance.computed_score ? (
                              <span className="font-medium">{instance.computed_score.toFixed(1)}%</span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {instance.sent_at ? new Date(instance.sent_at).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
